'use client';

/**
 * Fetched event-status banner (WEB-546).
 *
 * Renders the sticky honesty banner for a status that arrived with the event
 * payload (sold out, cancelled, postponed, offsale). REA-40: this surface does
 * not subscribe to `eventUpdates` and does not raise a live toast — nothing in
 * production publishes that channel.
 */

import { useEffect, useState, type ReactElement } from 'react';

export interface EventLiveStatusToastProps {
  /** Status from the fetched event. `null` when the payload has none. */
  initialStatus: string | null;
}

type StickyBannerKind = 'sold-out' | 'cancelled' | 'postponed' | 'offsale';

interface StickyBannerCopy {
  kind: StickyBannerKind;
  testId: string;
  message: string;
}

const STICKY_STATUS_COPY: Readonly<Record<string, StickyBannerCopy>> = {
  SOLD_OUT: {
    kind: 'sold-out',
    testId: 'event-live-sold-out-banner',
    message: 'Sold out. This event has reached capacity.',
  },
  CANCELLED: {
    kind: 'cancelled',
    testId: 'event-live-cancelled-banner',
    message: 'Cancelled. This event will not take place as listed.',
  },
  POSTPONED: {
    kind: 'postponed',
    testId: 'event-live-postponed-banner',
    message: 'Postponed. A new date has not been confirmed yet.',
  },
  OFFSALE: {
    kind: 'offsale',
    testId: 'event-live-offsale-banner',
    message: 'Sales ended. Tickets are no longer available for this event.',
  },
};

function resolveStickyBanner(status: string | null | undefined): StickyBannerCopy | null {
  if (!status) return null;
  return STICKY_STATUS_COPY[status.toUpperCase()] ?? null;
}

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(media.matches);
    const listener = (event: MediaQueryListEvent) => setReduced(event.matches);
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, []);
  return reduced;
}

export function EventLiveStatusToast({
  initialStatus,
}: EventLiveStatusToastProps): ReactElement | null {
  const reducedMotion = useReducedMotion();
  const banner = resolveStickyBanner(initialStatus);
  if (!banner) {
    return null;
  }

  const animationClass = reducedMotion
    ? 'transition-opacity duration-200'
    : 'animate-in slide-in-from-top duration-300';

  // WEB-1033 ruling: a per-kind status wash, `--danger` / `--warning` at 10% with
  // a same-hue hairline (the DS 50% border mix, `tokens/atoms.css:109-110`). The
  // wash is a gradient layer over an opaque `bg-background` because the banner is
  // sticky: a bare 10% fill would let scrolled content show through the message.
  const toneClass =
    banner.kind === 'sold-out'
      ? 'bg-warning text-foreground'
      : banner.kind === 'cancelled'
        ? 'bg-background bg-linear-to-b from-danger/10 to-danger/10 text-foreground border-b border-danger/50'
        : 'bg-background bg-linear-to-b from-warning/10 to-warning/10 text-foreground border-b border-warning/50';

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid={banner.testId}
      data-status-banner={banner.kind}
      data-reduced-motion={reducedMotion ? 'true' : 'false'}
      className={`sticky top-0 z-30 w-full px-4 py-3 text-center text-sm font-semibold shadow-md ${toneClass} ${animationClass}`}
    >
      {banner.message}
    </div>
  );
}
